<aside
  id="logo-sidebar"
  class="fixed flex flex-col overflow-hidden top-3 left-3 bottom-3 md:top-2 md:left-2 md:bottom-2 h-auto rounded-2xl sidebar-floating"
  [ngClass]="{
    'w-full md:w-[16rem]': isExpanded(),
    'w-0 md:w-[60px]': !isExpanded(),
    '-translate-x-full md:translate-x-0': !isExpanded(),
    'translate-x-0': isExpanded(),
    'z-[100] md:z-[60]': isPinned(),
    'z-[100]': !isPinned(),
    'sidebar-pinned': isPinned(),
  }"
  style="transition: width 0.4s var(--pc-ease-emphasized); contain: paint"
  (mouseenter)="onMouseEnter()"
  (mouseleave)="onMouseLeave()"
  aria-label="Sidebar"
>
  <!-- Sidebar Header -->
  <header class="shrink-0 flex items-center h-16 overflow-hidden relative">
    <!-- Expanded header -->
    <div
      class="absolute inset-0 flex items-center justify-between px-4 transition-opacity duration-300"
      [ngClass]="{ 'opacity-100': isExpanded(), 'opacity-0 pointer-events-none': !isExpanded() }"
    >
      <h3
        class="hidden md:block text-pc-title-sm font-semibold tracking-tight whitespace-nowrap animate-slide-in"
        style="color: var(--sidebar-text-active)"
      >
        {{ title() | translate }}
      </h3>
      <!-- Phone: who is signed in instead of the "Menú" title. -->
      <div class="md:hidden flex items-center gap-2.5 min-w-0">
        <span
          class="pc-avatar-token pc-avatar-token--house w-9 h-9 rounded-full flex items-center justify-center shrink-0 font-bold text-pc-label"
          aria-hidden="true"
          >{{ userInitials() }}</span
        >
        <div class="min-w-0">
          <p class="truncate text-[14px] font-semibold" style="color: var(--sidebar-text-active)">
            {{ userName() }}
          </p>
          <p class="truncate text-[12px]" style="color: var(--sidebar-text)">
            {{ userEmail() }}
          </p>
        </div>
      </div>
      <!-- Pin (desktop only) -->
      <button
        (click)="togglePin()"
        class="hidden md:inline-flex px-2 py-1 rounded-lg transition-colors cursor-pointer hover:bg-white/10"
        style="color: var(--sidebar-text)"
        [attr.title]="isPinned() ? 'Unpin sidebar' : 'Pin sidebar'"
      >
        <lucide-angular
          name="pin"
          [size]="16"
          class="transition-transform duration-300"
          [ngClass]="{
            'rotate-0': isPinned(),
            'rotate-45': !isPinned(),
            'opacity-60': !isPinned(),
          }"
        ></lucide-angular>
      </button>

      <!-- Close (mobile only) -->
      <button
        (click)="toggleMenu()"
        class="md:hidden inline-flex items-center justify-center w-8 h-8 rounded-lg transition-colors cursor-pointer hover:bg-white/10"
        style="color: var(--sidebar-text)"
        title="Close"
      >
        <lucide-angular name="x" [size]="16"></lucide-angular>
      </button>
    </div>
    <!-- Collapsed header -->
    <div
      class="absolute inset-0 flex items-center justify-center transition-opacity duration-300"
      [ngClass]="{ 'opacity-0 pointer-events-none': isExpanded(), 'opacity-100': !isExpanded() }"
    >
      <button
        (click)="toggleMenu()"
        class="p-2 rounded-xl transition-all cursor-pointer md:pointer-events-none"
        style="color: var(--sidebar-text)"
      >
        <lucide-angular name="panel-left" [size]="18"></lucide-angular>
      </button>
    </div>
  </header>

  <!-- Sidebar Navigation -->
  <!-- min-w while expanded pins the inner layout at full width during the
       width animation, so labels fade in instead of re-wrapping upwards. -->
  <nav
    class="flex-1 min-h-0 overflow-x-hidden sidebar-scrollable px-3 py-2"
    [ngClass]="{ 'md:min-w-[16rem]': isExpanded() }"
  >
    <ul class="flex flex-col gap-y-1">
      @for (item of items(); track $index) {
        @if (item.type === 'item') {
          <li class="relative">
            @if (item.disabled) {
              <!-- Disabled item: no routerLink, no hover effect, cursor not-allowed. -->
              <div
                class="flex items-center min-h-9 rounded-xl group relative outline-none opacity-60 cursor-not-allowed select-none"
                [ngClass]="{
                  'pr-2': isExpanded(),
                  'justify-center': !isExpanded(),
                }"
                [style.color]="'var(--sidebar-text)'"
                [attr.aria-disabled]="true"
              >
                @if (item.icon) {
                  <span class="flex items-center justify-center w-9 h-9 shrink-0">
                    @if (isSpriteIcon(item.icon)) {
                      <primecontrol-svg-icon
                        class="text-current"
                        [class.icon-size-4]="rowIconSize() === 16"
                        [class.icon-size-5]="rowIconSize() === 18"
                        [svgIcon]="item.icon"
                      ></primecontrol-svg-icon>
                    } @else {
                      <lucide-angular [name]="item.icon" [size]="rowIconSize()"></lucide-angular>
                    }
                  </span>
                }
                <span
                  class="text-pc-body font-medium overflow-hidden"
                  [ngClass]="{
                    'opacity-100 mr-3 max-w-full whitespace-normal break-words leading-tight transition-[opacity,margin] duration-300':
                      isExpanded(),
                    'opacity-0 max-w-0 whitespace-nowrap': !isExpanded(),
                  }"
                >
                  {{ item.title }}
                </span>

                @if (item.badge && isExpanded()) {
                  <span
                    class="sidebar-coming-soon-badge ml-auto mr-1 shrink-0 inline-flex items-center justify-center min-h-[18px] px-1.5 py-0 rounded-full text-pc-eyebrow font-semibold leading-none transition-opacity duration-300"
                  >
                    {{ item.badge | translate }}
                  </span>
                }
              </div>
            } @else {
              <a
                [routerLink]="[item.routerLink]"
                routerLinkActive="active"
                [routerLinkActiveOptions]="{ exact: item.exact || false }"
                #rla="routerLinkActive"
                (click)="onLinkClick()"
                class="sidebar-item flex items-center min-h-9 rounded-xl group relative outline-none"
                [ngClass]="{
                  'pr-2': isExpanded(),
                  'justify-center': !isExpanded(),
                  'sidebar-item-active font-semibold': rla.isActive || isItemActive(item),
                }"
                [style.color]="rla.isActive || isItemActive(item) ? null : 'var(--sidebar-text)'"
              >
                @if (item.icon) {
                  <span class="flex items-center justify-center w-9 h-9 shrink-0">
                    @if (isSpriteIcon(item.icon)) {
                      <primecontrol-svg-icon
                        class="text-current transition-transform duration-300 group-hover:scale-110"
                        [class.icon-size-4]="rowIconSize() === 16"
                        [class.icon-size-5]="rowIconSize() === 18"
                        [svgIcon]="item.icon"
                      ></primecontrol-svg-icon>
                    } @else {
                      <lucide-angular
                        [name]="item.icon"
                        [size]="rowIconSize()"
                        class="transition-transform duration-300 group-hover:scale-110"
                      ></lucide-angular>
                    }
                  </span>
                }

                <span
                  class="text-pc-body font-medium overflow-hidden"
                  [ngClass]="{
                    'opacity-100 mr-3 max-w-full whitespace-normal break-words leading-tight transition-[opacity,margin] duration-300':
                      isExpanded(),
                    'opacity-0 max-w-0 whitespace-nowrap': !isExpanded(),
                  }"
                >
                  {{ item.title }}
                </span>

                @if (item.badge && isExpanded()) {
                  <span
                    class="sidebar-new-badge ml-auto mr-1 shrink-0 inline-flex items-center justify-center min-h-[18px] px-1.5 py-0 rounded-full text-pc-eyebrow font-semibold leading-none text-white transition-opacity duration-300"
                  >
                    {{ item.badge | translate }}
                  </span>
                }

                <!-- Tooltip for closed state -->
                <div
                  class="absolute left-full ml-4 px-2 py-1 bg-neutral-900 text-white text-pc-label rounded pointer-events-none transition-opacity whitespace-nowrap z-50"
                  [ngClass]="{
                    'opacity-0': isExpanded(),
                    'opacity-0 group-hover:opacity-100': !isExpanded(),
                  }"
                >
                  {{ item.title }}
                </div>
              </a>
            }
          </li>
        } @else if (item.type === 'separator') {
          <li class="mt-2 mb-1 relative h-3">
            <span
              class="absolute inset-0 flex items-center px-3 text-pc-eyebrow font-bold uppercase tracking-wider whitespace-nowrap transition-opacity duration-300"
              style="color: var(--icon-color)"
              [ngClass]="{
                'opacity-100': isExpanded(),
                'opacity-0 pointer-events-none': !isExpanded(),
              }"
            >
              {{ item.title }}
            </span>
            <div
              class="absolute inset-0 flex items-center transition-opacity duration-300"
              [ngClass]="{
                'opacity-0 pointer-events-none': isExpanded(),
                'opacity-50': !isExpanded(),
              }"
            >
              <div
                class="h-px w-10 mx-auto"
                style="background-color: var(--sidebar-item-active-border)"
              ></div>
            </div>
          </li>
        }
      }
    </ul>
  </nav>

  <!-- Sidebar Footer -->
  <footer class="shrink-0 mt-auto overflow-hidden">
    <!-- h-16, the header's height: the version sits as far from the bottom edge
         as the header icon from the top one. -->
    <div
      class="px-4 h-16 flex items-center gap-3 max-md:border-t max-md:border-[color:var(--hairline)]"
      [ngClass]="{ 'justify-start': isExpanded(), 'justify-center': !isExpanded() }"
    >
      <!-- Phone: sign out at the bottom, where the thumb already is. -->
      <button
        type="button"
        class="md:hidden flex items-center gap-2 min-h-11 -ml-1 px-1 mr-auto text-[14px] font-semibold text-red-600 dark:text-red-400 cursor-pointer"
        (click)="signOut.emit()"
      >
        <lucide-angular name="log-out" [size]="18"></lucide-angular>
        {{ 'CLOSE_SESSION' | translate }}
      </button>
      <p class="text-pc-label whitespace-nowrap font-medium" style="color: var(--sidebar-text)">
        <span
          class="transition-opacity duration-300"
          [ngClass]="{
            'opacity-70': isExpanded(),
            'opacity-0 w-0 overflow-hidden inline-block': !isExpanded(),
          }"
          >{{ 'VERSION' | translate }}:
        </span>
        <span class="transition-opacity duration-300" [style.color]="'var(--sidebar-text-active)'">
          {{ appVersion() }}
        </span>
      </p>
    </div>
  </footer>
</aside>
